import { useEffect, useRef, useCallback } from "react";
import { Outlet, useLocation } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import CookieBanner from "./CookieBanner";
import SkipToContent from "./SkipToContent";
import { useLang } from "../i18n/context";
import { CookieConsentProvider } from "../lib/cookieConsent";
import PostHogProvider from "./PostHogProvider";
import HubSpotProvider from "./HubSpotProvider";

export default function Layout() {
  const { pathname } = useLocation();
  const { isTransitioning } = useLang();
  const progressRef = useRef<HTMLDivElement>(null);
  const barRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number>(0);

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  // Scroll progress bar — uses refs + rAF to avoid React re-renders
  const updateProgress = useCallback(() => {
    const scrollTop = window.scrollY;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    const progress = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;
    if (barRef.current) barRef.current.style.width = `${progress}%`;
    if (progressRef.current) progressRef.current.style.opacity = scrollTop > 100 ? "1" : "0";
  }, []);

  useEffect(() => {
    function handleScroll() {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(updateProgress);
    }
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      cancelAnimationFrame(rafRef.current);
    };
  }, [updateProgress]);

  return (
    <div className="min-h-screen bg-[#111111] text-white">
      <SkipToContent />
      <CookieConsentProvider>
        <PostHogProvider>
          <HubSpotProvider>
        {/* Scroll progress bar */}
        <div
          ref={progressRef}
          className="fixed top-0 left-0 right-0 z-[60] h-[2px] pointer-events-none opacity-0"
          style={{ transition: "opacity 0.2s ease" }}
        >
          <div
            ref={barRef}
            className="h-full"
            style={{
              width: "0%",
              backgroundColor: "var(--color-brand-primary)",
              willChange: "width",
            }}
          />
        </div>
        <Header />
        <div
          id="main-content"
          className="lang-crossfade"
          style={{
            opacity: isTransitioning ? 0 : 1,
            filter: isTransitioning ? "blur(6px)" : "none",
            transform: isTransitioning ? "scale(0.998)" : "none",
          }}
        >
          <main>
            <Outlet />
          </main>
          <Footer />
        </div>
        <CookieBanner />
          </HubSpotProvider>
        </PostHogProvider>
      </CookieConsentProvider>
    </div>
  );
}
